import { createAsyncThunk } from "@reduxjs/toolkit"
import type { ApiResponse } from "apisauce"

import { errorSave } from "../../../redux/errors.slice"
import { type RootState } from "../../../store.ts"
import {
  type BaseApiResponse,
  type BasePagination
} from "../../../types/api.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import type { PermissionsBlockingUsersResponse } from "../../SettingsPermissions/redux/permissions.types.ts"
import * as services from "../api/services.ts"
import type { TeamMember, TeamMemberMutationData } from "../types/types.ts"
import { teamMembersSliceName } from "./team-members.constants.ts"
import type {
  TeamMembersFilterOptions,
  TeamMembersFilters
} from "./team-members.types.ts"

export const fetchTeamMembersFilterOptions = createAsyncThunk<
  TeamMembersFilterOptions,
  void
>(
  `${teamMembersSliceName}/fetchTeamMembersFilterOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getUsersFilterOptions()
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to fetch users filter options" }
      )
    }
    return response.data
  }
)

export const fetchTeamMembers = createAsyncThunk<
  BasePagination<TeamMember>,
  Partial<TeamMembersFilters> & { isMore?: boolean }
>(
  `${teamMembersSliceName}/fetchTeamMembers`,
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.getUsers(params)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to fetch users" }
      )
    }
    return response.data
  }
)

export const createTeamMember = createAsyncThunk<
  TeamMember,
  TeamMemberMutationData
>(
  `${teamMembersSliceName}/createTeamMember`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.createUser(params)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to create user" }
      )
    }

    showResponseMessage(response, "User created successfully.")

    return response.data
  }
)

export const updateTeamMember = createAsyncThunk<void, TeamMemberMutationData>(
  `${teamMembersSliceName}/updateTeamMember`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = (await services.newUpdateUser(params)) as ApiResponse<
      BaseApiResponse<unknown, { blocking?: PermissionsBlockingUsersResponse }>
    >

    if (!response.ok) {
      const errorDetails = response?.data?.details
      const isBlockingError = !!errorDetails?.blocking

      if (!isBlockingError) dispatch(errorSave(errorDetails))
      return rejectWithValue(errorDetails || { errors: "Unknown error" })
    }

    showResponseMessage(response, "User updated successfully.")
  }
)

export const deleteTeamMember = createAsyncThunk<{ id: string }, string>(
  `${teamMembersSliceName}/deleteTeamMember`,
  async (userId, { dispatch, rejectWithValue }) => {
    const response = (await services.deleteUser(userId)) as ApiResponse<
      BaseApiResponse<unknown, { blocking?: PermissionsBlockingUsersResponse }>
    >

    if (!response.ok) {
      const errorDetails = response?.data?.details
      const isBlockingError = !!errorDetails?.blocking

      if (!isBlockingError) dispatch(errorSave(errorDetails))
      return rejectWithValue(errorDetails || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Team member removed.")

    return { id: userId }
  }
)

export const fetchTeamMember = createAsyncThunk<TeamMember, string>(
  `${teamMembersSliceName}/fetchTeamMember`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getUserDetails(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to fetch user details" }
      )
    }
    return response.data
  }
)

export const performActionTeamMember = createAsyncThunk<
  void,
  { id: string; action: string },
  { state: RootState }
>(
  `${teamMembersSliceName}/performActionTeamMember`,
  async (params, { dispatch, getState, rejectWithValue }) => {
    const response = await services.performActionUser(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to perform action on user" }
      )
    }

    const filters = getState().users.filters
    await dispatch(fetchTeamMembers(filters))

    showResponseMessage(response, "User action performed successfully.")
  }
)

export const fetchInviteTeamMemberInfo = createAsyncThunk<any, any, any>(
  `${teamMembersSliceName}/fetchInviteTeamMemberInfo`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getInviteUserInfo(params)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(
        response.data || { message: "Failed to fetch invite user info" }
      )
    }
    return response.data
  }
)
